import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  completeTeacherInvite,
  teacherLogin,
  teacherLogout,
  validateAdminToken,
  type CompleteTeacherInviteInput,
} from '../api';
import {
  clearAdminToken,
  readAdminToken,
  subscribeAdminUnauthorized,
  type AdminUnauthorizedReason,
  writeAdminToken,
} from '../lib/adminToken';
import {
  type AppRole,
  resolveInitialAppRole,
  writeSessionAppRole,
} from '../lib/appRole';
import {
  clearTeacherSession,
  subscribeTeacherUnauthorized,
} from '../lib/teacherSession';

// Read the flag locally (same as `appRole.ts`) so `vi.resetModules()` + env stubs
// re-evaluate correctly; do not snapshot `apiConfig.USE_LIVE` through the façade.
const USE_LIVE = import.meta.env.VITE_USE_LIVE_API === 'true';

const AUTH_NOTICE_BY_REASON: Record<AdminUnauthorizedReason, string> = {
  expired: 'Din lærer-adgang er udløbet eller ugyldig. Log ind igen.',
  disabled: 'Læreradgang er ikke aktiveret på serveren.',
};

export interface AppRoleContextValue {
  appRole: AppRole;
  setAppRole: (role: AppRole) => void;
  loginAsTeacher: () => void;
  loginWithToken: (token: string, signal?: AbortSignal) => Promise<void>;
  loginWithEmail: (email: string, password: string, signal?: AbortSignal) => Promise<void>;
  signupWithInvite: (input: CompleteTeacherInviteInput) => Promise<void>;
  logoutToStudent: () => Promise<void>;
  isTeacher: boolean;
  authNotice: string | null;
  clearAuthNotice: () => void;
  useLiveAuth: boolean;
}

const AppRoleContext = createContext<AppRoleContextValue | null>(null);

export function AppRoleProvider({ children }: { children: ReactNode }) {
  const [appRole, setAppRoleState] = useState<AppRole>(resolveInitialAppRole);
  const [authNotice, setAuthNotice] = useState<string | null>(null);

  const setAppRole = useCallback((role: AppRole) => {
    setAppRoleState(role);
    writeSessionAppRole(role);
  }, []);

  const clearAuthNotice = useCallback(() => {
    setAuthNotice(null);
  }, []);

  const loginAsTeacher = useCallback(() => {
    if (USE_LIVE) {
      return;
    }
    setAppRole('teacher');
  }, [setAppRole]);

  const loginWithToken = useCallback(async (token: string, signal?: AbortSignal) => {
    await validateAdminToken(token, signal);
    if (signal?.aborted) {
      return;
    }
    clearTeacherSession();
    writeAdminToken(token);
    clearAuthNotice();
    setAppRole('teacher');
  }, [clearAuthNotice, setAppRole]);

  const loginWithEmail = useCallback(async (email: string, password: string, signal?: AbortSignal) => {
    await teacherLogin(email, password);
    clearAdminToken();
    if (signal?.aborted) {
      return;
    }
    clearAuthNotice();
    setAppRole('teacher');
  }, [clearAuthNotice, setAppRole]);

  const signupWithInvite = useCallback(async (input: CompleteTeacherInviteInput) => {
    // `completeTeacherInvite` clears the admin token and stores the teacher
    // session; the role state must follow in the same tick, otherwise the route
    // guards treat the brand-new teacher as a student until the page reloads.
    await completeTeacherInvite(input);
    clearAuthNotice();
    setAppRole('teacher');
  }, [clearAuthNotice, setAppRole]);

  const logoutToStudent = useCallback(async () => {
    const logout = teacherLogout();
    if (readAdminToken()) {
      clearAdminToken();
    }
    clearAuthNotice();
    setAppRole('student');
    await logout;
  }, [clearAuthNotice, setAppRole]);

  useEffect(() => {
    return subscribeAdminUnauthorized((reason) => {
      setAuthNotice(AUTH_NOTICE_BY_REASON[reason]);
      setAppRoleState('student');
      writeSessionAppRole('student');
    });
  }, []);

  useEffect(() => {
    return subscribeTeacherUnauthorized(() => {
      setAuthNotice(AUTH_NOTICE_BY_REASON.expired);
      setAppRoleState('student');
      writeSessionAppRole('student');
    });
  }, []);

  const value = useMemo<AppRoleContextValue>(
    () => ({
      appRole,
      setAppRole,
      loginAsTeacher,
      loginWithToken,
      loginWithEmail,
      signupWithInvite,
      logoutToStudent,
      isTeacher: appRole === 'teacher',
      authNotice,
      clearAuthNotice,
      useLiveAuth: USE_LIVE,
    }),
    [
      appRole,
      setAppRole,
      loginAsTeacher,
      loginWithToken,
      loginWithEmail,
      signupWithInvite,
      logoutToStudent,
      authNotice,
      clearAuthNotice,
    ],
  );

  return createElement(AppRoleContext.Provider, { value }, children);
}

export function useAppRole(): AppRoleContextValue {
  const value = useContext(AppRoleContext);
  if (!value) {
    throw new Error('useAppRole must be used within AppRoleProvider');
  }
  return value;
}
